/*
 * CustomerPortalClient — ported from CustomerPortalClient.js (1245 lines).
 * Singleton API client: auth, credentials, all backend endpoints, fetcher engine.
 */

import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "sonner";
import { base_url, active_context, mapValues, _t, set_toast_fn, set_i18n_fn } from "./templating";
import {
    activeCtx,
    dataCtx,
    dictionary,
    staticData,
    responses,
    fetchData,
    s3_active_credentials,
    user_ref,
    formData,
    filtersPersistent,
    actions,
    refs,
    promises,
    resolve_entry,
} from "./state";
import { _ } from "./i18n";
import { init_i18n } from "./i18n";
import type { DashboardSchema, Fetcher } from "./types";

const base_url_v2 = base_url + "/api/v2/";
const auth_url = base_url + "/auth";
const logout_url = base_url + "/auth/logout";
const token_url = base_url_v2 + "token";
const info_url = base_url_v2 + "info";
const dashboard_url = base_url + "/dashboard";
const dashboards_url = base_url + "/dashboards";
const dashboards_all_url = base_url + "/dashboards/all";
const theme_url = base_url + "/theme";
const table_metadata_url = base_url + "/user/table/metadata";
const theme_url_css = base_url + "/theme.css";
const theme_url_logo = base_url + "/theme.logo";
const user_details_url = base_url + "/user/details";
const list_permissions_url = base_url + "/permissions";
const publish_url = base_url + "/publish";
const i18n_url = base_url + "/i18n";
const tasks_url = base_url + "/task";
const edit_quota_url = base_url + "/quotas";
const dashboard_upload_url = base_url + "/dashboards/import";
const dashboard_download_url = base_url + "/dashboards/export";

/* S3 fetchers — imported lazily to avoid circular dependency */
let s3_fetchers: unknown[] = [];
let do_s3_request: ((fetcher: Record<string, unknown>, params: Record<string, unknown>) => Promise<unknown>) | null = null;

export const set_s3_handlers = (
    fetchers: unknown[],
    handler: (fetcher: Record<string, unknown>, params: Record<string, unknown>) => Promise<unknown>
) => {
    s3_fetchers = fetchers;
    do_s3_request = handler;
};

const toast_fn = (severity: string, summary: string, detail: string, life?: number) => {
    const sonner_type = severity === "error" ? "error" : severity === "warn" ? "warning" : severity === "success" ? "success" : "info";
    toast[sonner_type](detail, { description: summary, duration: life });
};

set_toast_fn(toast_fn);

export class CustomerPortalClient {
    token: string | undefined;

    constructor() {
        this.token = undefined;
        init_i18n(this);
        set_i18n_fn(_);
    }

    handleError = (err: { response?: { status?: number; data?: unknown }; code?: number }): any => {
        let status: number | undefined;
        if (err && err.response) {
            status = err.response.status;
        }
        if (status === 401) {
            if (this.token) {
                toast.warning(_t("Session expired!"), {
                    description: _t("Please click here to login again."),
                    action: {
                        label: _t("Login"),
                        onClick: () => this.do_auth(),
                    },
                });
            }
            this.token = undefined;
            Cookies.remove("id_token");
            Cookies.remove("active-context");
            if (err.response?.data === "The request you did requires authentication.") {
                this.do_auth();
            }
        }
        return "err";
    };

    do_auth() {
        const redirect_uri = window.location.pathname;
        Cookies.set("active-path", redirect_uri);
        window.location.href = `${auth_url}`;
    }

    do_logout() {
        Cookies.remove("active-context");
        Cookies.remove("active-path");
        Cookies.remove("id_token");
        Cookies.remove("region");
        window.location.href = logout_url;
    }

    save_context(val: string[] | string) {
        Cookies.set("active-context", Array.isArray(val) ? val.join(",") : val);
    }

    load_context(): string[] | undefined {
        const ctx = Cookies.get("active-context");
        if (ctx) {
            return ctx.split(",");
        }
        return undefined;
    }

    get_context(activeContext: { ctx?: string[] }): string[] {
        let ctx = activeContext.ctx;
        if (!ctx || ctx.length === 0) {
            ctx = this.load_context();
            if (!ctx) {
                ctx = [];
            }
        }
        return ctx;
    }

    async get_credentials(region: string | null = null): Promise<{ headers: Record<string, string | undefined> }> {
        if (!this.token) {
            this.token = Cookies.get("id_token") || undefined;
        }
        const ctx = active_context();
        if (!this.token) {
            const path = window.location.href;
            if (path.indexOf("/#id_token=") > -1) {
                this.token = path.split("/#id_token=")[1];
                Cookies.remove("id_token");
                Cookies.set("id_token", this.token!, { expires: 30 });
                const active_path = Cookies.get("active-path");
                if (active_path) {
                    window.location.href = active_path;
                } else {
                    window.history.replaceState({}, "", "/");
                }
                return Promise.resolve({
                    headers: {
                        Authorization: "Bearer " + this.token,
                        Reseller: ctx.reseller,
                        Client: ctx.client,
                        region: region || ctx.region || Cookies.get("region"),
                    },
                });
            } else {
                this.do_auth();
                return Promise.resolve({
                    headers: {
                        Authorization: "Bearer " + this.token,
                        Reseller: ctx.reseller,
                        Client: ctx.client,
                        region: region || ctx.region || Cookies.get("region"),
                    },
                });
            }
        }
        return Promise.resolve({
            headers: {
                Authorization: "Bearer " + this.token,
                Reseller: ctx.reseller,
                Client: ctx.client,
                region: region || ctx.region || Cookies.get("region"),
            },
        });
    }

    async get_table_metadatas(table_id: string) {
        return this.get_credentials().then((cred) => {
            return axios.get(table_metadata_url + "/" + table_id, cred).then((res) => res.data).catch(console.error);
        });
    }

    async set_table_metadatas(table_id: string, metadata: unknown) {
        return this.get_credentials().then((cred) => {
            return axios.post(table_metadata_url + "/" + table_id, metadata, cred).then((res) => res.data).catch(console.error);
        });
    }

    async get_languages() {
        return this.get_credentials().then((cred) => {
            return axios.get(i18n_url + "/langs", cred).then((res) => res.data);
        }).catch((err) => this.handleError(err));
    }

    async set_quota(data: unknown) {
        return this.get_credentials().then((cred) => {
            return axios.post(edit_quota_url, data, cred).then((res) => res.data);
        }).catch((err) => this.handleError(err));
    }

    async upload_dashboard(file: File) {
        const fd = new FormData();
        fd.append("file", file);
        return this.get_credentials().then((cred) => {
            return axios.post(dashboard_upload_url, fd, cred).then((res) => res.data);
        }).catch((err) => this.handleError(err));
    }

    do_download(res: unknown, file_name: string | null = null) {
        let blob: Blob;
        if (typeof res === "string") {
            blob = new Blob([res], { type: "application/octet-stream" });
        } else if (res instanceof Blob) {
            blob = res;
        } else if (res !== null && typeof res === "object") {
            blob = new Blob([JSON.stringify(res)], { type: "application/octet-stream" });
        } else {
            blob = new Blob([String(res)], { type: "application/octet-stream" });
        }
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", file_name || "export.dashboards");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }

    async download_dashboard() {
        return this.get_credentials().then((cred) => {
            (cred as Record<string, unknown>).responseType = "blob";
            return axios.get(dashboard_download_url, cred).then((res) => {
                this.do_download(res.data);
            });
        }).catch((err) => this.handleError(err));
    }

    async update_token(val: string, token: string) {
        return this.get_credentials().then((cred) => {
            (cred.headers as Record<string, string | undefined>)["Content-type"] = "application/json";
            dictionary[token || val] = val;
            return axios.post(i18n_url + `/lang/` + dataCtx.lang, JSON.stringify({ key: token || val, value: val }), cred).then((res) => res.data).catch(console.trace);
        }).catch((err) => this.handleError(err));
    }

    async save_token(val: string, token: string | null) {
        dictionary[token || val] = val;
        return this.get_credentials().then((cred) => {
            (cred.headers as Record<string, string | undefined>)["Content-type"] = "application/json";
            return axios.post(i18n_url + `/lang/EN-US?insert=true`, JSON.stringify({ key: token || val, value: val }), cred).then((res) => res.data).catch(console.trace);
        }).catch((err) => this.handleError(err));
    }

    async get_tokens() {
        return this.get_credentials().then((cred) => {
            return axios.get(i18n_url + `/lang/${dataCtx.lang}`, cred).then((res) => res.data);
        }).catch((err) => this.handleError(err));
    }

    async get_contexts() {
        return this.get_credentials().then((cred) => {
            return axios.get(base_url + "/context", cred).then((resp) => resp.data);
        }).catch((err) => this.handleError(err));
    }

    async get_regions() {
        return this.get_credentials().then((cred) => {
            return axios.get(base_url + "/context/region", cred).then((resp) => resp.data);
        }).catch((err) => this.handleError(err));
    }

    async get_dashboards() {
        return this.get_credentials().then((cred) => {
            return axios.get(base_url + "/dashboards", cred).then((resp) => resp.data);
        }).catch((err) => this.handleError(err));
    }

    async get_quota_profiles() {
        return this.get_credentials().then((cred) => {
            return axios.get(base_url + "/profile/quota", cred).then((resp) => resp.data);
        }).catch((err) => this.handleError(err));
    }

    async get_all_dashboards() {
        return this.get_credentials().then((cred) => {
            return axios.get(dashboards_all_url, cred).then((resp) => resp.data);
        }).catch((err) => this.handleError(err));
    }

    async set_dashboard(dashboard: unknown) {
        return this.get_credentials().then((cred) => {
            return axios.post(dashboard_url, dashboard, cred).then((resp) => resp.data);
        }).catch((err) => this.handleError(err));
    }

    async delete_dashboard(dashboard: unknown) {
        return this.get_credentials().then((cred) => {
            return axios.delete(dashboard_url, { data: dashboard, ...cred }).then((resp) => resp.data);
        }).catch((err) => this.handleError(err));
    }

    async set_element(element: unknown) {
        return this.get_credentials().then((cred) => {
            return axios.post(dashboard_url + "/element", element, cred).then((resp) => resp.data);
        }).catch((err) => this.handleError(err));
    }

    async get_elements() {
        return this.get_credentials().then((cred) => {
            return axios.get(dashboard_url + "/elements", cred).then((resp) => resp.data);
        }).catch((err) => this.handleError(err));
    }

    async refresh_theme(theme: unknown, reseller?: string, client_id?: string) {
        return this.get_credentials().then((cred) => {
            const theme_name = user_ref.user?.theme || "bright";
            const url = theme_url + "?theme=" + theme_name + (reseller ? `&reseller_id=${reseller}` : "") + (client_id ? `&client_id=${client_id}` : "");
            (cred.headers as Record<string, string | undefined>)["Content-type"] = "application/json";
            return axios.post(url, theme, cred).then((resp) => resp.data);
        }).catch((err) => this.handleError(err));
    }

    async get_active_credential(project_id: string): Promise<Record<string, string>> {
        const cached_cred = s3_active_credentials[project_id] as Record<string, string> | undefined;
        if (cached_cred) {
            return Promise.resolve(cached_cred);
        }
        return this.get_credentials().then((cred) => {
            const url = base_url + `/ec2?resource_id=${project_id}`;
            (cred.headers as Record<string, string | undefined>)["Content-type"] = "application/json";
            return axios.get(url, cred).then((resp) => {
                if (!resp.data || !resp.data.access_key) {
                    return {} as Record<string, string>;
                }
                s3_active_credentials[project_id] = resp.data;
                return s3_active_credentials[project_id] as Record<string, string>;
            });
        }).catch((err) => this.handleError(err));
    }

    async get_data_fetch(methods = ["get"]) {
        return this.list_permissions().then((resp: { access: Record<string, Record<string, unknown>> }) => {
            const options = resp.access;
            const entries: unknown[] = [];
            for (const key of Object.keys(options)) {
                for (const method of methods) {
                    const val = options[key][method] as {
                        path_params?: Record<string, unknown>;
                        body_params?: { required?: Record<string, unknown>; optional?: Record<string, unknown> };
                        description?: string;
                        permission?: string;
                    } | undefined;
                    if (!val) continue;
                    const path_params = val.path_params || {};
                    let body_params = val.body_params || {};
                    body_params = { ...(body_params.required || {}), ...(body_params.optional || {}) };
                    entries.push({
                        name: val.description,
                        fetcher: key,
                        method,
                        permission: val.permission,
                        params: { ...path_params, ...body_params },
                    });
                }
            }
            entries.push({
                name: "Http request",
                fetcher: "",
                method: "",
                permission: "",
                params: { URL: "string", METHOD: "string", HEADERS: "json", PARAMS: "json" },
            });
            return entries.concat(s3_fetchers);
        });
    }

    async get_theme(reseller?: string, client_id?: string) {
        return this.get_credentials().then((cred) => {
            const theme_name = user_ref.user?.theme || "bright";
            const url = theme_url + "?theme=" + theme_name + (reseller ? `&reseller_id=${reseller}` : "") + (client_id ? `&client_id=${client_id}` : "");
            return axios.get(url, cred).then((resp) => {
                if (typeof window !== "undefined") {
                    setTimeout(() => {
                        document.title = resp.data.title;
                    }, 1000);
                }
                return resp.data;
            });
        }).catch((err) => this.handleError(err));
    }

    async get_user_details() {
        return this.get_credentials().then((cred) => {
            return axios.get(user_details_url, cred).then((resp) => resp.data);
        }).catch((err) => this.handleError(err));
    }

    async get_tasks() {
        return this.get_credentials().then((cred) => {
            return axios.get(tasks_url + "?unread=true", cred).then((resp) => {
                return axios.get(base_url + `/notification?unread=true`, cred).then((resp2) => {
                    return { notification: resp2.data, tasks: resp.data };
                });
            });
        }).catch((err) => this.handleError(err));
    }

    async get_task_logs(task_id: string) {
        return this.get_credentials().then((cred) => {
            return axios.get(tasks_url + `/${task_id}/logs`, cred).then((resp) => resp.data);
        }).catch((err) => this.handleError(err));
    }

    async get_task_steps(task_id: string) {
        return this.get_credentials().then((cred) => {
            return axios.get(tasks_url + `/${task_id}/step`, cred).then((resp) => resp.data);
        }).catch((err) => this.handleError(err));
    }

    async create_task(task_name: string) {
        return this.get_credentials().then((cred) => {
            return axios.post(tasks_url, { name: task_name }, cred).then((resp) => resp.data);
        }).catch((err) => this.handleError(err));
    }

    async run_task(task_id: string) {
        return this.get_credentials().then((cred) => {
            return axios.post(tasks_url + `/${task_id}/run`, "", cred).then((resp) => resp.data);
        }).catch((err) => this.handleError(err));
    }

    async read_task(task_id: string) {
        return this.get_credentials().then((cred) => {
            return axios.post(tasks_url + `/${task_id}`, "", cred).then((resp) => resp.data);
        }).catch((err) => this.handleError(err));
    }

    async read_notification(task_id: string) {
        return this.get_credentials().then((cred) => {
            return axios.patch(base_url + `/notification`, { id: task_id }, cred).then((resp) => resp.data);
        }).catch((err) => this.handleError(err));
    }

    async create_task_step(task_id: string, path: string, params: unknown, method: string, step_id: string, description: string) {
        return this.get_credentials().then((cred) => {
            const url = tasks_url + `/${task_id}/step`;
            const data = { step_path: path, params: JSON.stringify(params), method, description, step_id };
            return axios.post(url, data, cred).then((resp) => resp.data);
        }).catch((err) => this.handleError(err));
    }

    async fetch_datas(
        fetchers: any[],
        dataContext: Record<string, unknown>,
        idx = 0,
        task_name: string | null = null,
        task_id: string | null = null
    ): Promise<unknown> {
        let _idx = idx;

        if (task_name && !task_id) {
            return this.create_task(task_name).then((resp: { id: string }) => {
                return this.fetch_datas(fetchers, dataContext, _idx, task_name, resp.id);
            });
        }
        if (_idx >= fetchers.length) {
            if (task_id) {
                return this.run_task(task_id);
            }
            let last_idx = _idx - 1;
            while (last_idx >= 0 && !(`response_${last_idx}` in dataContext)) {
                last_idx -= 1;
            }
            return dataContext[`response_${last_idx}`];
        }
        _idx += 1;
        const context: Record<string, unknown> = { ...dataContext, ...dataCtx };
        const { success, error, use_response, message_level, err_message_level, iterate, skip } = fetchers[idx];
        if (skip) {
            if (mapValues(skip, context, true)) {
                return this.fetch_datas(fetchers, dataContext, _idx, task_name, task_id);
            }
        }
        let it: unknown[] = [{}];
        if (iterate) {
            it = mapValues(iterate, context, true) as unknown[];
        }
        const resp: Promise<unknown>[] = [];

        const fetcher: Record<string, unknown> = fetchers[idx].fetcher;
        const ops = fetchers[idx].operations;
        const entry = mapValues(fetchers[idx].entry, dataContext) as string | undefined;
        if (entry) {
            staticData[entry] = staticData[entry] || null;
        }
        const { raise_error, enable_cache } = fetchers[idx];
        const download_response: string | boolean | null = fetchers[idx].download ?? null;
        fetcher.cookie = fetchers[idx].cookie;
        fetcher.region = fetchers[idx].region;
        for (const _it of it) {
            try {
                const _ctx_keys = Object.keys(context);
                const params: Record<string, unknown> = Object.fromEntries(((fetchers[idx].params as { key: string; value: string }[]) || []).map((v) => [v.key, v.value]));
                for (const p of Object.keys(params)) {
                    const _v = params[p] as string;
                    if (typeof _v === "string" && _v.indexOf("#") >= 0) {
                        params[p] = mapValues(_v, { ...context, it: _it }, true) as string;
                        continue;
                    }
                    if (_ctx_keys.indexOf(_v) === -1) {
                        continue;
                    }
                    const _val = mapValues(_v, { ...context, it: _it }, true);
                    if (_val === _v) {
                        params[p] = (context as Record<string, unknown>)[_val as string];
                    } else {
                        params[p] = _val as string;
                    }
                }
                const rsp = this.fetch_data(fetcher, { ...dataContext, it: _it }, params, entry, enable_cache ?? null, ops, task_id, _idx, raise_error ?? null, download_response).then((resp_data: unknown) => {
                    const df: Record<string, unknown> = { ...dataContext, ...dataCtx, it: _it };
                    if (success) {
                        const severity = message_level || "success";
                        toast_fn(severity, _(severity), String(mapValues(_(success), { ...dataContext, it: _it })), 30000);
                    }
                    if (download_response) {
                        if (download_response === "straight") {
                            return [];
                        }
                        this.do_download(resp_data, mapValues(download_response, { ...dataContext, it: _it }) as string);
                    } else {
                        df[`response_${idx}`] = resp_data;
                    }
                    return this.fetch_datas(fetchers, df, _idx, task_name, task_id);
                }).catch((err: { response?: { data?: unknown }; data?: unknown }) => {
                    let _errmsg: unknown = (err.response || {}).data || err;
                    if (_errmsg && typeof _errmsg === "object" && "Error" in (_errmsg as object)) {
                        _errmsg = (_errmsg as { Error: string }).Error;
                    }
                    const severity = err_message_level || "error";
                    if (error) {
                        toast_fn(severity, _(severity), `${mapValues(_(error), context)}: ${_errmsg}`, 600000);
                    } else {
                        toast_fn(severity, _(severity), `${_errmsg}`, 600000);
                    }
                    this.handleError(err as never);
                    return "";
                });
                resp.push(rsp);
            } catch (err) {
                let _errmsg: unknown = (err as { response?: { data?: unknown } }).response?.data || err;
                if (_errmsg && typeof _errmsg === "object" && "Error" in (_errmsg as object)) {
                    _errmsg = (_errmsg as { Error: string }).Error;
                }
                if (raise_error && error){
                    toast_fn("error", _("Error"), `${mapValues(_(error), context)}`, 600000);
                    return "";
                }
                if (error) {
                    toast_fn("error", _("Error"), `${mapValues(_(error), context)}: ${_errmsg}`, 600000);
                } else {
                    toast_fn("error", _("Error"), `${_errmsg}`, 600000);
                }
                return "";
            }
        }
        if (use_response) {
            return dataContext["response_" + use_response];
        }
        if (resp.length === 1) {
            return resp[0];
        }
        return Promise.all(resp);
    }

    async fetch_data(
        fetcher: Record<string, unknown>,
        dataContext: Record<string, unknown>,
        params: Record<string, unknown>,
        entry?: string,
        enable_cache: boolean | null = null,
        ops: unknown = null,
        task_id: string | null = null,
        step_id: string | number | null = null,
        raise_error: string | null = null,
        download_response: string | boolean | null = null
    ): Promise<unknown> {
        const dc = dataContext as Record<string, unknown>;
        const _step_id = String(step_id || "");
        const _entry = entry || "";
        if (enable_cache === true && _entry) {
            const _cache = staticData[_entry];
            if (_cache) {
                return Promise.resolve(_cache);
            }
        }
        if (fetcher.fetcher === "local_operation") {
            const data = mapValues(ops, { ...dc }, true);
            if (_entry && enable_cache !== false) {
                staticData[_entry] = data;
            }
            if (enable_cache === false) {
                delete staticData[_entry];
            }
            if (raise_error && mapValues(raise_error, { ...dc, response: data }, true)) {
                throw "err";
            }
            return Promise.resolve(data);
        }
        let _params: Record<string, unknown> = {};
        if (params) {
            _params = { ...params };
        } else if (fetcher.params) {
            _params = { ...(fetcher.params as Record<string, unknown>) };
        }
        let _url = fetcher.fetcher as string;
        for (const entry_key of Object.keys(_params)) {
            try {
                let rep = _params[entry_key] as unknown;
                if (typeof rep === "string" && isNaN(Number(rep)) && rep.indexOf("dataContext") >= 0) {
                    // eslint-disable-next-line no-eval
                    rep = eval(rep);
                    _params[entry_key] = rep;
                }
                const old_url = _url;
                _url = _url.replaceAll(`<${entry_key}>`, String(rep));
                if (old_url !== _url && !task_id) {
                    delete _params[entry_key];
                }
            } catch (e) {
                throw `${e} - ${JSON.stringify(_params)} - ${entry_key}`;
            }
        }
        if (fetcher.name === "Http request") {
            const method = (_params.METHOD as string).toLowerCase();
            const url = _params.URL as string;
            const headers = _params.HEADERS as Record<string, string>;
            const http_params = _params.params;
            return (axios as any)[method](url, { data: http_params, headers, withCredentials: fetcher.cookie }).then((resp_data: { data: unknown }) => {
                let data = resp_data.data;
                fetchData["response_" + (parseInt(_step_id) - 1)] = data;
                if (ops) {
                    data = mapValues(ops, { ...dc, response: data }, true);
                }
                responses["response_" + (parseInt(_step_id) - 1)] = data;
                if (raise_error && mapValues(raise_error, { ...dc, response: data }, true)) {
                    throw "err";
                }
                if (_entry && enable_cache !== false) {
                    staticData[_entry] = data;
                    resolve_entry(_entry, data);
                }
                if (enable_cache === false) {
                    delete staticData[_entry];
                }
                return data;
            });
        }
        if (typeof fetcher.name === "string" && (fetcher.name as string).indexOf("S3 - ") === 0) {
            if (!do_s3_request) throw new Error("S3 handler not initialized");
            return do_s3_request(fetcher, _params).then((s3_resp: unknown) => {
                let data = s3_resp;
                fetchData["response_" + (parseInt(_step_id) - 1)] = data;
                if (ops) {
                    data = mapValues(ops, { ...dc, response: data }, true);
                }
                responses["response_" + (parseInt(_step_id) - 1)] = data;
                if (raise_error && mapValues(raise_error, { ...dc, response: data }, true)) {
                    throw "err";
                }
                if (_entry && enable_cache !== false) {
                    staticData[_entry] = data;
                    resolve_entry(_entry, data);
                }
                if (enable_cache === false) {
                    delete staticData[_entry];
                }
                return data;
            });
        }
        return this.get_credentials(fetcher.region as string | null).then((cred) => {
            const url = base_url + _url;
            (cred as Record<string, unknown>).withCredentials = fetcher.cookie;
            if (task_id) {
                return this.create_task_step(task_id, _url, _params, fetcher.method as string, _step_id, fetcher.name as string);
            }
            if (fetcher.method === "get") {
                const query_params: [string, unknown][] = [];
                for (const [ent, val] of Object.entries(_params)) {
                    query_params.push([ent, val]);
                }
                const query_string = query_params.map(([k, v]) => k + "=" + v).join("&");
                const full_url = query_string ? url + "?" + query_string : url;
                if (download_response === "straight") {
                    window.open(full_url + "&id_token=" + this.token, "_blank");
                    return [];
                }
                return axios.get(full_url, cred).then((resp_data: { data: unknown }) => {
                    let data = resp_data.data;
                    fetchData["response_" + (parseInt(_step_id) - 1)] = data;
                    if (ops) {
                        data = mapValues(ops, { ...dc, response: data }, true);
                    }
                    responses["response_" + (parseInt(_step_id) - 1)] = data;
                    if (raise_error && mapValues(raise_error, { ...dc, response: data }, true)) {
                        throw "err";
                    }
                    if (_entry && enable_cache !== false) {
                        staticData[_entry] = data;
                        resolve_entry(_entry, data);
                    }
                    if (enable_cache === false) {
                        delete staticData[_entry];
                    }
                    return data;
                });
            } else if (fetcher.method === "delete") {
                return axios.delete(url, { data: _params, ...cred }).then((resp_data: { data: unknown }) => {
                    let data = resp_data.data;
                    fetchData["response_" + (parseInt(_step_id) - 1)] = data;
                    if (ops) {
                        data = mapValues(ops, { ...dc, response: data }, true);
                    }
                    responses["response_" + (parseInt(_step_id) - 1)] = data;
                    if (_entry) {
                        staticData[_entry] = data;
                        resolve_entry(_entry, data);
                    }
                    return data;
                });
            } else {
                return (axios as any)[fetcher.method as string](url, _params, cred).then((resp_data: { data: unknown }) => {
                    let data = resp_data.data;
                    fetchData["response_" + (parseInt(_step_id) - 1)] = data;
                    if (ops) {
                        data = mapValues(ops, { ...dc, response: data }, true);
                    }
                    responses["response_" + (parseInt(_step_id) - 1)] = data;
                    if (_entry) {
                        staticData[_entry] = data;
                        resolve_entry(_entry, data);
                    }
                    return data;
                });
            }
        });
    }

    async list_permissions() {
        return this.get_credentials().then((cred) => {
            return axios.get(list_permissions_url, cred).then((resp) => resp.data);
        }).catch((err) => this.handleError(err));
    }

    async set_user_details(details: unknown) {
        return this.get_credentials().then((cred) => {
            return axios.post(user_details_url, details, cred).then((resp) => resp.data);
        }).catch((err) => this.handleError(err));
    }

    async get_theme_css(reseller?: string, client_id?: string) {
        return this.get_credentials().then((cred) => {
            const theme_name = user_ref.user?.theme || "bright";
            const url = theme_url + "?theme=" + theme_name + (reseller ? `&reseller_id=${reseller}` : "") + (client_id ? `&client_id=${client_id}` : "");
            return axios.get(url, cred).then((resp) => resp.data.css);
        }).catch((err) => this.handleError(err));
    }

    async get_theme_logo(reseller?: string, client_id?: string) {
        return this.get_credentials().then((cred) => {
            const theme_name = user_ref.user?.theme || "bright";
            const url = theme_url + "?theme=" + theme_name + (reseller ? `&reseller_id=${reseller}` : "") + (client_id ? `&client_id=${client_id}` : "");
            return axios.get(url, cred).then((resp) => resp.data.logo);
        }).catch((err) => this.handleError(err));
    }

    async get_component(path: string): Promise<DashboardSchema | number> {
        return this.get_credentials().then((cred) => {
            const url = base_url + path;
            return axios.get(url, cred).then((resp) => resp.data).catch((err) => err.response.status);
        }).catch((err) => this.handleError(err));
    }

    async toggle_theme() {
        return this.get_credentials().then((cred) => {
            const url = user_details_url + "/theme";
            return axios.patch(url, {}, cred).then((resp) => resp.data).catch((err) => err.response.status);
        }).catch((err) => this.handleError(err));
    }

    async get_version() {
        return axios.get(base_url).then((resp) => resp.data).catch((err) => err.response.status);
    }
}

const customerPortalClient = new CustomerPortalClient();
export default customerPortalClient;
